/**
 * <VaultImage />
 * ---------------------------------------------------------------
 * Resolver-driven image component. Given a Content ID (SW-000123),
 * calls the Vault resolver to get the current thumb/original URL
 * and renders it. When an asset is later replaced in the Vault,
 * every <VaultImage /> using its Content ID picks up the new bytes
 * automatically after the resolver cache TTL (30 s public, none for
 * admin).
 *
 * Props
 *   contentId  — required. e.g. "SW-000012"
 *   variant    — "thumb" | "original"  (default "thumb")
 *   alt        — accessible alt text. Falls back to the resolved metadata.
 *   className  — passthrough <img> class.
 *   admin      — true when rendered inside the Founder Portal. Uses the
 *                admin resolver + attaches the ?auth= token to the src.
 *   onResolved — optional callback receiving the full resolver payload.
 */
import { useEffect, useState } from "react";
import { adminApi, API_BASE, readAdminToken } from "@/lib/adminApi";

const CACHE = new Map(); // contentId -> resolved payload (admin path)

function withAuth(url) {
  const token = readAdminToken();
  if (!token) return url;
  // resolver returns paths like "/api/admin/vault/file/{id}?variant=thumb"
  const sep = url.includes("?") ? "&" : "?";
  return `${API_BASE}${url}${sep}auth=${encodeURIComponent(token)}`;
}

export default function VaultImage({
  contentId,
  variant = "thumb",
  alt,
  className = "",
  admin = true,
  onResolved,
  "data-testid": testId,
}) {
  const [state, setState] = useState({ loading: true, url: null, meta: null, error: null });

  useEffect(() => {
    if (!contentId) { setState({ loading: false, url: null, meta: null, error: null }); return; }
    let cancelled = false;

    (async () => {
      // Cached hit for the same contentId (invalidated when the app reloads
      // — that's fine, we want a fresh resolve on the next full nav anyway).
      if (admin && CACHE.has(contentId)) {
        const meta = CACHE.get(contentId);
        const raw = variant === "thumb" ? meta.thumb_url : meta.original_url;
        if (!cancelled) {
          setState({ loading: false, url: withAuth(raw), meta, error: null });
          onResolved?.(meta);
        }
        return;
      }
      try {
        const meta = await adminApi.resolveAsset(contentId, admin);
        if (admin) CACHE.set(contentId, meta);
        const raw = variant === "thumb" ? meta.thumb_url : meta.original_url;
        const url = admin ? withAuth(raw) : `${API_BASE}${raw}`;
        if (!cancelled) {
          setState({ loading: false, url, meta, error: null });
          onResolved?.(meta);
        }
      } catch (e) {
        if (!cancelled) setState({ loading: false, url: null, meta: null, error: e.message || "resolve failed" });
      }
    })();

    return () => { cancelled = true; };
  }, [contentId, variant, admin]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!contentId) return null;

  if (state.loading) {
    return (
      <div
        className={`animate-pulse bg-white/[0.04] border border-white/10 ${className}`}
        data-testid={testId || "vault-image-loading"}
      />
    );
  }
  if (state.error || !state.url) {
    return (
      <div
        className={`flex items-center justify-center border border-red-400/30 bg-red-400/[0.03] text-[10px] tracking-[0.24em] uppercase text-red-300/70 ${className}`}
        data-testid={testId || "vault-image-error"}
        title={state.error || "not found"}
      >
        {contentId}
      </div>
    );
  }
  return (
    <img
      src={state.url}
      alt={alt || state.meta?.alt || state.meta?.title || contentId}
      className={className}
      data-testid={testId || "vault-image"}
      loading="lazy"
    />
  );
}

/** Invalidate the in-memory resolver cache — call after replace/upload. */
export function invalidateVaultImage(contentId) {
  if (contentId) CACHE.delete(contentId);
  else CACHE.clear();
}
